import React from 'react';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { EFP_COVER_CATEGORIES } from '@om/redux/actions/easiplusFuneralPlan/types';
import {
    clearAllLivesCovered,
    setAddChildValue,
    setAddPartnerValue,
    setAgeValue,
    setCoverAmountValue,
    setCoverCategoryValue,
    setCoverMemberValue,
    setNewCoverExtendedFamily,
    setNewCoverMyself,
    setNewCoverParents,
    setShowingAgeValidation
} from '@om/redux/actions/easiplusFuneralPlan/quote';
import {
    getAddChild,
    getAddPartner,
    getAge,
    getAgeHintText,
    getAgePlaceholderText,
    getCoverAmount,
    getCoverAmountLimits,
    getDirectFamilyIsSelected,
    getDisplayCoverAmount,
    getDisplayCoverCategories,
    getDisplayCoverMembers,
    getDisplayMonthlyPremium,
    getLivesCoveredDrawerIsOpen,
    getRelationship,
    getSelectedCoverCategory,
    getShowingAgeValidation,
    getSliderSteps,
} from '@om/redux/selectors/easiplusFuneralPlan/quote';
import {
    OmFormDropdownFieldWrapper,
    OmFormInputFieldWrapper,
    OmSlider,
    OmCheckBox,
    OmLoader,
} from '@om/component-library-react';
import QuoteSideDrawerWrapper from './QuoteSideDrawerWrapper';
import { DEFAULT_VALIDATION_DELAY } from '../../../config';
import {
    getIsQuoteReferenceDataValid,
    getIsApplicationReferenceDataValid,
} from '@om/redux/selectors/easiplusFuneralPlan/referenceData';

const FormContent = () => {
    const dispatch = useDispatch();
    const {
        livesCoveredDrawerIsOpen,
        displayCoverCategories,
        selectedCoverCategory,
        directFamilySelected,
        age,
        addPartner,
        addChild,
        coverAmount,
        displayCoverAmount,
        displayMonthlyPremium,
        displayCoverMembers,
        selectedMember,
        coverAmountLimits,
        ageHintText,
        agePlaceholderText,
        showingAgeValidation,
        sliderSteps,
    } = useSelector(
        createStructuredSelector({
            livesCoveredDrawerIsOpen: getLivesCoveredDrawerIsOpen,
            displayCoverCategories: getDisplayCoverCategories,
            selectedCoverCategory: getSelectedCoverCategory,
            directFamilySelected: getDirectFamilyIsSelected,
            age: getAge,
            addPartner: getAddPartner,
            addChild: getAddChild,
            coverAmount: getCoverAmount,
            displayCoverAmount: getDisplayCoverAmount,
            displayMonthlyPremium: getDisplayMonthlyPremium,
            displayCoverMembers: getDisplayCoverMembers,
            selectedMember: getRelationship,
            coverAmountLimits: getCoverAmountLimits,
            ageHintText: getAgeHintText,
            agePlaceholderText: getAgePlaceholderText,
            showingAgeValidation: getShowingAgeValidation,
            sliderSteps: getSliderSteps,
        })
    );

    const selectCoverCategory = (category) => {
        dispatch(setCoverCategoryValue(category));
        switch (category) {
        case EFP_COVER_CATEGORIES.PARENTS_AND_IN_LAWS:
            return dispatch(setNewCoverParents());
        case EFP_COVER_CATEGORIES.DIRECT_FAMILY:
            return dispatch(setNewCoverMyself());
        case EFP_COVER_CATEGORIES.EXTENDED_FAMILY:
            return dispatch(setNewCoverExtendedFamily());
        default:
            return;
        }
    };

    return (
        <>
            <QuoteSideDrawerWrapper
                drawerIsOpen={livesCoveredDrawerIsOpen}
                editCategory={selectedCoverCategory.value}
                onClearAll={() => dispatch(clearAllLivesCovered())}
            >
                <h6 className="no-top-margin medium">Who would you like to cover?</h6>
                <OmFormDropdownFieldWrapper
                    align="left"
                    placeholder="Select a cover category"
                    placeholderAsLabel="false"
                    formId="addLivesCovered"
                    size="full"
                    name="coverCategory"
                    required
                    useNativeMobileDropdown
                    value={selectedCoverCategory && selectedCoverCategory.value}
                    state={selectedCoverCategory && selectedCoverCategory.status}
                    errorMessage={selectedCoverCategory && selectedCoverCategory.error}
                    options={JSON.stringify(
                        displayCoverCategories &&
                            displayCoverCategories.map((category) => ({
                                value: category.value,
                                label: category.value,
                                disabled: category.disabled,
                            }))
                    )}
                    onOnSelect={(event) => selectCoverCategory(event.detail.value)}
                />

                {selectedCoverCategory &&
                    selectedCoverCategory.value &&
                    displayCoverMembers.length > 0 && (
                    <OmFormDropdownFieldWrapper
                        align="left"
                        label=" "
                        placeholder="What's your relationship?"
                        placeholderAsLabel="false"
                        formId="addInLaws"
                        size="full"
                        name="Relationship"
                        useNativeMobileDropdown
                        required
                        key={selectedCoverCategory.value}
                        value={selectedMember && selectedMember.value}
                        state={selectedMember && selectedMember.status}
                        errorMessage={selectedMember && selectedMember.error}
                        options={JSON.stringify(
                            displayCoverMembers &&
                                displayCoverMembers.map((coverCategory) => ({
                                    value: coverCategory.value,
                                    label: coverCategory.value,
                                    disabled: coverCategory.disabled,
                                }))
                        )}
                        onOnSelect={(event) =>
                            dispatch(setCoverMemberValue(event.detail.value))
                        }
                    />
                )}

                <div>
                    {(displayCoverMembers && displayCoverMembers.length
                        ? selectedMember.value
                        : selectedCoverCategory.value) && (
                        <>
                            <OmFormInputFieldWrapper
                                id="ageField"
                                mask="number"
                                size="full"
                                formId="addLivesCovered"
                                name="Age"
                                align="left"
                                label=" "
                                required
                                placeholder={agePlaceholderText}
                                hintText={ageHintText}
                                validationDelay={DEFAULT_VALIDATION_DELAY}
                                value={age && age.value}
                                state={age && age.status}
                                errorMessage={age && age.error}
                                onShowingValidation={(event) =>
                                    dispatch(setShowingAgeValidation(event.detail))
                                }
                                onPersist={(event) => dispatch(setAgeValue(event.detail))}
                            />

                            <div>
                                {directFamilySelected &&
                                    age.status === 'valid' &&
                                    showingAgeValidation && (
                                    <>
                                        <h6 className="no-bottom-margin">
                                            Who would you like to cover?
                                        </h6>
                                        <OmCheckBox
                                            text=" Add my Spouse/Partner"
                                            checked={addPartner && addPartner.value}
                                            onOnCheckBoxChanged={(event) =>
                                                dispatch(setAddPartnerValue(event.detail))
                                            }
                                        />
                                        <OmCheckBox
                                            text=" Add my Child/Children"
                                            checked={addChild && addChild.value}
                                            onOnCheckBoxChanged={(event) =>
                                                dispatch(setAddChildValue(event.detail))
                                            }
                                        />
                                    </>
                                )}
                            </div>

                            <div>
                                {age && age.status === 'valid' && showingAgeValidation && (
                                    <>
                                        <h6 className="no-bottom-margin">
                                            Select your cover amount:
                                        </h6>
                                        <OmSlider
                                            stepString={JSON.stringify(sliderSteps)}
                                            minLabel={coverAmountLimits.minLabel}
                                            maxLabel={coverAmountLimits.maxLabel}
                                            value={coverAmount.value}
                                            onSliderChange={event => dispatch(setCoverAmountValue(event.detail))}
                                        />
                                        <div className="cover-label-wrapper">
                                            <span className="cover-label">
                                                <h5 className="no-bottom-margin no-top-margin medium">
                                                    {displayCoverAmount}
                                                </h5>
                                                <h6 className="small medium no-top-margin">
                                                    COVER AMOUNT
                                                </h6>
                                            </span>

                                            <span className="premium-label">
                                                <h5 className="no-bottom-margin no-top-margin medium">
                                                    {displayMonthlyPremium}
                                                </h5>
                                                <h6 className="small medium no-top-margin">
                                                    TOTAL MONTHLY PREMIUM
                                                </h6>
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </QuoteSideDrawerWrapper>
        </>
    );
};

const loadingText = (livesCoveredDrawerIsOpen) => {
    return (
        <>
            <QuoteSideDrawerWrapper
                drawerIsOpen={livesCoveredDrawerIsOpen}
                editCategory={EFP_COVER_CATEGORIES.DIRECT_FAMILY}
            >
                <OmLoader loaderText={'Loading...'} isLoading={true} />
            </QuoteSideDrawerWrapper>
        </>
    );
};

const LivesCoveredQuoteDrawer = () => {
    const { livesCoveredDrawerIsOpen, quoteReferenceDataValid, applicationReferenceDataValid } = useSelector(
        createStructuredSelector({
            livesCoveredDrawerIsOpen: getLivesCoveredDrawerIsOpen,
            quoteReferenceDataValid: getIsQuoteReferenceDataValid,
            applicationReferenceDataValid: getIsApplicationReferenceDataValid,
        })
    );

    if (applicationReferenceDataValid && quoteReferenceDataValid) {
        return (<FormContent/>); 
    }

    return (
        <div>
            {loadingText(livesCoveredDrawerIsOpen)}
        </div>
    );
};

export default LivesCoveredQuoteDrawer;
